import api from 'src/utils/ajax';
import { getCopiedTitle } from 'src/utils/stringFormatter';

import {
  denormalizeAppAlert,
  normalizeAppAlert,
  normalizeAppAlertsResponse,
} from './normalizers';

import type { AppAlert, FetchAppAlertsResponse } from './_types';

interface FetchAdminAppAlertsOptions {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const fetchAdminAppAlerts = async ({
  page = 1,
  perPage = 15,
  search,
  sortBy,
  sortDir,
}: FetchAdminAppAlertsOptions): Promise<FetchAppAlertsResponse> => {
  const response = await api.get('/api/v4/modal_alerts', {
    params: {
      page,
      per: perPage,
      ...(search && { 'q[title_cont]': search }),
      ...(sortBy && sortDir && { 'q[s]': `${sortBy} ${sortDir}` }),
    },
  });

  return normalizeAppAlertsResponse(response.data);
};

export const deleteAppAlert = async (
  alertId: AppAlert['id']
): Promise<void> => {
  await api.delete(`/api/v4/modal_alerts/${alertId}`);
};

export const duplicateAppAlert = async (
  alert: Partial<AppAlert>
): Promise<AppAlert> => {
  const response = await api.post('/api/v4/modal_alerts', {
    modal_alert: denormalizeAppAlert({
      ...alert,
      ...(alert.title ? { title: getCopiedTitle(alert.title) } : {}),
    }),
  });

  return normalizeAppAlert(response.data);
};

export const fetchAppAlert = async (
  alertId: AppAlert['id']
): Promise<AppAlert> => {
  const response = await api.get(`/api/v4/modal_alerts/${alertId}`);
  return normalizeAppAlert(response.data);
};

type SaveAppAlertPayload = Partial<AppAlert>;
export const createAppAlert = async (
  alert: SaveAppAlertPayload
): Promise<AppAlert> => {
  const response = await api.post('/api/v4/modal_alerts', {
    modal_alert: denormalizeAppAlert(alert),
  });

  return normalizeAppAlert(response.data);
};

export const updateAppAlert = async (
  alertId: AppAlert['id'],
  alert: SaveAppAlertPayload
): Promise<AppAlert> => {
  const response = await api.put(`/api/v4/modal_alerts/${alertId}`, {
    modal_alert: denormalizeAppAlert(alert),
  });

  return normalizeAppAlert(response.data);
};

export const uploadAppAlertImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);

  const response = await api.post(
    '/api/v2/assets/image/workout-plan-share?size=original',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data.url;
};
